import React, { useState, useEffect, useRef } from 'react';
import { Input } from './input';

interface CurrencyInputProps {
  id?: string;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  placeholder?: string;
  min?: number;
}

export const CurrencyInput: React.FC<CurrencyInputProps> = ({
  id,
  value,
  onChange,
  disabled = false,
  readOnly = false,
  className = '',
  placeholder = '0.00',
  min = 0,
}) => {
  const [displayValue, setDisplayValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Format number with thousand separators and 2 decimal places
  const formatCurrency = (num: number): string => {
    return num.toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Update display value when value prop changes or focus changes
  useEffect(() => {
    if (isFocused) {
      // When focused, show raw number for editing
      setDisplayValue(value === 0 ? '' : value.toString());
    } else {
      // When not focused, show formatted value
      setDisplayValue(formatCurrency(value));
    }
  }, [value, isFocused]);

  const handleFocus = () => {
    setIsFocused(true);
    // Show raw number for editing
    setDisplayValue(value === 0 ? '' : value.toString());
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Parse and validate the input
    const numValue = parseFloat(displayValue);
    if (isNaN(numValue) || numValue < min) {
      onChange(0);
      setDisplayValue(formatCurrency(0));
    } else {
      onChange(numValue);
      setDisplayValue(formatCurrency(numValue));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Allow empty string, numbers, and one decimal point
    if (inputValue === '' || /^\d*\.?\d*$/.test(inputValue)) {
      setDisplayValue(inputValue);
      
      // Update the actual value if it's a valid number
      const numValue = parseFloat(inputValue);
      if (!isNaN(numValue) && numValue >= min) {
        onChange(numValue);
      } else if (inputValue === '') {
        onChange(0);
      }
    }
  };

  return (
    <Input
      ref={inputRef}
      id={id}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      readOnly={readOnly}
      className={className}
      placeholder={placeholder}
    />
  );
};
